/** The parts that turn while a machine works (an extractor's ring). */
import * as THREE from 'three';

import type { SpinAxis } from '../models/part';
import type { Instances } from './instances';

/** A turning part: whoever placed it may pause it (an extractor that is full). */
export interface Spin {
  paused: boolean;
}

interface Spinner extends Spin {
  name: string;
  index: number;
  base: THREE.Matrix4;
  axis: THREE.Vector3;
  speed: number;
}

export interface Spinners {
  clear(): void;
  /** A placed part that turns about its own `axis` at `speed` radians a second. */
  add(name: string, index: number, base: THREE.Matrix4, axis: SpinAxis, speed: number): Spin;
  /** Turn them all; true while there is something to turn. */
  animate(now: number, reduced: boolean): boolean;
}

const AXES: Readonly<Record<SpinAxis, THREE.Vector3>> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};
const _r = new THREE.Matrix4();
const _m = new THREE.Matrix4();

export function createSpinners(instances: Instances): Spinners {
  const list: Spinner[] = [];
  return {
    clear() {
      list.length = 0;
    },
    add(name, index, base, axis, speed) {
      const spinner = { name, index, base: base.clone(), axis: AXES[axis], speed, paused: false };
      list.push(spinner);
      return spinner;
    },
    animate(now, reduced) {
      if (reduced) return false;
      let turning = false;
      for (const s of list) {
        if (s.paused) continue;
        _r.makeRotationAxis(s.axis, (now / 1000) * s.speed);
        _m.copy(s.base).multiply(_r);
        instances.setMatrixAt(s.name, s.index, _m);
        turning = true;
      }
      return turning;
    },
  };
}
